import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import AdminHeader from '@/components/feature/AdminHeader';
import LoadingState from '@/components/base/LoadingState';
import { useNotifications, type Notification, type NotificationCategory } from '@/hooks/useNotifications';

const CATEGORIES: { value: NotificationCategory | 'all'; label: string; icon: string }[] = [
  { value: 'all',         label: 'All',         icon: 'ri-notification-3-line' },
  { value: 'lead',        label: 'Leads',       icon: 'ri-user-received-line' },
  { value: 'payment',     label: 'Payments',    icon: 'ri-money-dollar-circle-line' },
  { value: 'signature',   label: 'Signatures',  icon: 'ri-file-text-line' },
  { value: 'addon',       label: 'Add-ons',     icon: 'ri-puzzle-line' },
  { value: 'delivery',    label: 'Deliveries',  icon: 'ri-send-plane-line' },
  { value: 'appointment', label: 'Appointments', icon: 'ri-calendar-event-line' },
  { value: 'system',      label: 'System',      icon: 'ri-settings-3-line' },
];

const typeColor: Record<string, string> = {
  info:    'text-sky-500',
  success: 'text-emerald-500',
  warning: 'text-amber-500',
  error:   'text-rose-500',
};

const typeBg: Record<string, string> = {
  info:    'bg-sky-50',
  success: 'bg-emerald-50',
  warning: 'bg-amber-50',
  error:   'bg-rose-50',
};

const catIconMap: Record<string, string> = {
  lead:        'ri-user-received-line',
  payment:     'ri-money-dollar-circle-line',
  signature:   'ri-file-text-line',
  addon:       'ri-puzzle-line',
  delivery:    'ri-send-plane-line',
  appointment: 'ri-calendar-event-line',
  system:      'ri-settings-3-line',
};

const catColorMap: Record<string, string> = {
  lead:        'text-sky-600 bg-sky-50 border-sky-100',
  payment:     'text-emerald-600 bg-emerald-50 border-emerald-100',
  signature:   'text-violet-600 bg-violet-50 border-violet-100',
  addon:       'text-amber-600 bg-amber-50 border-amber-100',
  delivery:    'text-teal-600 bg-teal-50 border-teal-100',
  appointment: 'text-pink-600 bg-pink-50 border-pink-100',
  system:      'text-slate-600 bg-slate-50 border-slate-100',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'Yesterday' : `${d}d ago`;
}

function entityRoute(n: Notification): string | null {
  if (!n.entity_type || !n.entity_id) return null;
  switch (n.entity_type) {
    case 'contract':     return `/admin/contracts/${n.entity_id}`;
    case 'payment':      return `/admin/payments`;
    case 'appointment':  return `/admin/appointments`;
    case 'lead':         return `/admin/leads/${n.entity_id}`;
    case 'job':          return `/admin/jobs/${n.entity_id}`;
    case 'client':       return `/admin/clients/${n.entity_id}`;
    default:             return null;
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<NotificationCategory | 'all'>('all');
  const [readFilter, setReadFilter] = useState<'all' | 'unread' | 'read'>('all');

  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications(category);

  const filtered = notifications.filter((n) => {
    if (readFilter === 'unread') return !n.is_read;
    if (readFilter === 'read') return n.is_read;
    return true;
  });

  const unreadFiltered = filtered.filter((n) => !n.is_read).length;

  return (
    <AdminLayout>
      <AdminHeader
        title="Notifications"
        subtitle="All your activity alerts and system messages"
        actions={
          unreadCount > 0 ? (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-double-line text-sm"></i>
              </span>
              Mark all read
            </button>
          ) : undefined
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7 max-w-5xl">
        {/* ── Category filter chips ── */}
        <div className="flex items-center gap-1.5 flex-wrap mb-4">
          {CATEGORIES.map((c) => {
            const isActive = category === c.value;
            const catUnread = notifications.filter((n) => !n.is_read && (c.value === 'all' || n.category === c.value)).length;
            return (
              <button
                key={c.value}
                onClick={() => setCategory(c.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-[#C8A96E] text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${c.icon} text-sm`}></i>
                </span>
                {c.label}
                {catUnread > 0 && (
                  <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/30 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {catUnread}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Read/unread tabs ── */}
        <div className="flex items-center gap-1 bg-white rounded-lg border border-slate-200 p-1 w-fit mb-6">
          {[
            { value: 'all' as const, label: 'All' },
            { value: 'unread' as const, label: `Unread ${unreadFiltered > 0 ? `(${unreadFiltered})` : ''}` },
            { value: 'read' as const, label: 'Read' },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setReadFilter(tab.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                readFilter === tab.value
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── List ── */}
        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-50">
              <i className="ri-notification-off-line text-slate-300 text-3xl"></i>
            </div>
            <div className="text-center">
              <h3 className="text-slate-700 font-semibold text-sm mb-1">No notifications</h3>
              <p className="text-slate-400 text-xs">
                {readFilter === 'unread'
                  ? 'You have no unread notifications in this category.'
                  : readFilter === 'read'
                  ? 'No read notifications yet.'
                  : 'Activity alerts will appear here as they happen.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((n) => {
              const route = entityRoute(n);
              const catStyle = n.category ? (catColorMap[n.category] ?? catColorMap.system) : catColorMap.system;

              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-4 bg-white rounded-xl border transition-all ${
                    !n.is_read
                      ? 'border-slate-200 hover:border-[#C8A96E]/30'
                      : 'border-slate-100 hover:border-slate-200'
                  } px-5 py-4`}
                >
                  {/* Icon */}
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${typeBg[n.type] ?? typeBg.info}`}>
                    <i className={`${
                      n.category && catIconMap[n.category]
                        ? catIconMap[n.category]
                        : (typeColor[n.type] ?? typeColor.info)
                    } text-lg`}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className={`text-sm font-semibold truncate ${!n.is_read ? 'text-slate-800' : 'text-slate-400'}`}>
                            {n.title}
                          </p>
                          {!n.is_read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C8A96E] shrink-0"></span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                          {n.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {n.category && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${catStyle}`}>
                              <i className={`${catIconMap[n.category] ?? 'ri-notification-3-line'} text-[9px]`}></i>
                              {n.category.charAt(0).toUpperCase() + n.category.slice(1)}
                            </span>
                          )}
                          <span className="text-[10px] text-slate-300">{timeAgo(n.created_at)}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {!n.is_read && (
                          <button
                            onClick={() => markAsRead(n.id)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#C8A96E] hover:bg-[#C8A96E]/5 rounded-lg transition-all cursor-pointer"
                            title="Mark as read"
                          >
                            <i className="ri-check-line text-sm"></i>
                          </button>
                        )}
                        {route && (
                          <button
                            onClick={() => navigate(route)}
                            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-lg transition-all cursor-pointer"
                            title="View"
                          >
                            <i className="ri-arrow-right-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}